import React, { useEffect, useState } from 'react';
import 'dayjs/locale/fr';
import { Paper, CircularProgress } from '@mui/material';
import { getDashboardInfos, listEmployees } from '../api/restaurant';
import { getUpcomingEvents, scrapeAndSaveEvents, getFootballMatches, scrapeAndSaveMatches } from '../api/events';
import { ScrapedEvent } from '../types/api/ScrapedEvent';
import { FootballMatch } from '../types/api/FootballMatch';
import TodaysStats from '../types/api/TodaysStats';
import Employee from '../types/api/Employee';
import TabsDashboard from '../components/Dashboard/TabsDashboard';
import TabPerformances from '../components/Dashboard/TabPerformances';
import TabPrevisions from '../components/Dashboard/TabPrevisions';
import TabRH from '../components/Dashboard/TabRH';
import TabInformations from '../components/Dashboard/TabInformations';
import WeatherSlide from '../components/Weather/WeatherSlide';
import '../styles/dashboard.css';
import { loadParams } from '../tools/utils';

interface DashboardProps {}

function Dashboard(DashboardProps: DashboardProps) {
  const [dashboardInfos, setDashboardInfos] = useState<TodaysStats | undefined>();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [events, setEvents] = useState<ScrapedEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [matches, setMatches] = useState<FootballMatch[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<'perf' | 'rh' | 'info' | 'prev'>('perf');
  const freeTodayNoon = dashboardInfos?.freeTodayNoon ?? [];
  const freeTodayEvening = dashboardInfos?.freeTodayEvening ?? [];
  const [myCity, setMyCity] = useState<string>("");


  useEffect(() => {
    const loadCity = async () => {
      const value = await loadParams();
      setMyCity(value.get("city") || "");
    };

    loadCity();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await getDashboardInfos();
        if (res?.code === 200 && res.data) setDashboardInfos(res.data);
        const empRes = await listEmployees();
        if (empRes?.code === 200 && empRes.data) setEmployees(empRes.data);
      } catch (e) { console.error(e); }
    };
    init();
  }, []);

  useEffect(() => {
    const load = async () => {
      setEventsLoading(true);
      setMatchesLoading(true);
      try {
        const ev = await getUpcomingEvents();
        if (ev?.events) setEvents(ev.events);
        const mm = await getFootballMatches();
        if (mm?.matches) setMatches(mm.matches);
      } catch (e) { console.error(e); }
      finally { setEventsLoading(false); setMatchesLoading(false); }
    };
    load();
  }, []);

  const refreshMatches = async () => {
    setMatchesLoading(true);
    try {
      const res = await scrapeAndSaveMatches();
      if (res?.matches) setMatches(res.matches);
    } finally { setMatchesLoading(false); }
  };

  const refreshEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await scrapeAndSaveEvents();
      if (res?.events) setEvents(res.events);
    } finally { setEventsLoading(false); }
  };


  return (
    <div className="dp-root">
      <div className="dp-container">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          {/* Tab buttons */}
          <TabsDashboard currentTab={currentTab} setCurrentTab={setCurrentTab} />
          <div className="hidden md:flex items-center justify-end text-sm">
            <WeatherSlide city={myCity} compact />
          </div>
        </div>

        {/* Content */}
        <div>
          {currentTab === 'perf' && <TabPerformances dashboardInfos={dashboardInfos} />}
          {currentTab === 'prev' && <TabPrevisions dashboardInfos={dashboardInfos} />}
          {currentTab === 'rh' && <TabRH dashboardInfos={dashboardInfos} freeTodayNoon={freeTodayNoon} freeTodayEvening={freeTodayEvening} employees={employees} />}
          {currentTab === 'info' && (
            <TabInformations
              matches={matches}
              matchesLoading={matchesLoading}
              onRefreshMatches={refreshMatches}
              events={events}
              eventsLoading={eventsLoading}
              onRefreshEvents={refreshEvents}
              WeatherSlide={WeatherSlide}
            />
          )}
        </div>

        {/* small loader if nothing */}
        {!dashboardInfos && (
          <Paper className="dp-card" elevation={2}>
            <div className="flex items-center gap-4">
              <CircularProgress />
              <span>Chargement des données...</span>
            </div>
          </Paper>
        )}
      </div>
    </div>
  );
};

export default Dashboard;